import { cn } from '@/lib/utils';
import { useEditorStore, useTerminalStore, useUIStore, useWorkspaceStore } from '@/store';
import { useGitStore } from '@/store/gitStore';
import { useWalletStore } from '@/store/walletStore';
import { ChevronUp, Circle, GitBranch, Wallet, Wifi, WifiOff, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import LifecycleIndicator from './LifecycleIndicator';
import NotificationPanel, { NotificationBell } from './NotificationPanel';

/* ================================================================== */
/*  Chain metadata (mirrors DeployPanel)                              */
/* ================================================================== */

const CHAINS = [
  { chainId: 11155111, name: 'Ethereum Sepolia', logo: '⟠', color: '#627EEA' },
  { chainId: 1, name: 'Ethereum', logo: '⟠', color: '#627EEA' },
  { chainId: 421614, name: 'Arbitrum Sepolia', logo: '🔵', color: '#28A0F0' },
  { chainId: 42161, name: 'Arbitrum One', logo: '🔵', color: '#28A0F0' },
  { chainId: 80002, name: 'Polygon Amoy', logo: '🟣', color: '#8247E5' },
  { chainId: 137, name: 'Polygon', logo: '🟣', color: '#8247E5' },
  { chainId: 84532, name: 'Base Sepolia', logo: '🔷', color: '#0052FF' },
  { chainId: 8453, name: 'Base', logo: '🔷', color: '#0052FF' },
  { chainId: 11155420, name: 'OP Sepolia', logo: '🔴', color: '#FF0420' },
  { chainId: 10, name: 'Optimism', logo: '🔴', color: '#FF0420' },
  { chainId: 43113, name: 'Avax Fuji', logo: '🔺', color: '#E84142' },
  { chainId: 43114, name: 'Avalanche', logo: '🔺', color: '#E84142' },
  { chainId: 97, name: 'BSC Testnet', logo: '🟡', color: '#F0B90B' },
  { chainId: 56, name: 'BNB Chain', logo: '🟡', color: '#F0B90B' },
] as const;

/* ================================================================== */
/*  Chain Selector Popover                                            */
/* ================================================================== */

function ChainSelector({
  selectedChainId,
  onSelect,
  isOpen,
  onClose,
}: {
  selectedChainId: number;
  onSelect: (chainId: number) => void;
  isOpen: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className="absolute bottom-7 right-0 w-52 bg-[#252526] border border-[#454545] rounded-lg shadow-2xl py-1 z-50 max-h-72 overflow-y-auto"
    >
      <div className="px-2 py-1 text-[9px] text-[#666] font-semibold uppercase tracking-wider">
        Select Chain
      </div>
      {CHAINS.map((chain) => (
        <button
          key={chain.chainId}
          onClick={() => {
            onSelect(chain.chainId);
            onClose();
          }}
          className={cn(
            'w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] transition-colors',
            chain.chainId === selectedChainId
              ? 'bg-[#04395e] text-white'
              : 'text-[#cccccc] hover:bg-[#2a2d2e]',
          )}
        >
          <span className="text-sm">{chain.logo}</span>
          <span className="flex-1">{chain.name}</span>
          <span className="text-[9px] text-[#666]">{chain.chainId}</span>
        </button>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Status Bar                                                        */
/* ================================================================== */

export default function StatusBar() {
  const { activeWorkspace } = useWorkspaceStore();
  const { tabs, activeTabId } = useEditorStore();
  const { isConnected } = useTerminalStore();
  const { setActivePanel } = useUIStore();
  const {
    isConnected: walletConnected,
    address: walletAddress,
    connect: connectWallet,
  } = useWalletStore();
  const { branch: gitBranch } = useGitStore();
  const [selectedChainId, setSelectedChainId] = useState(11155111);
  const [showChainSelector, setShowChainSelector] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const selectedChain = CHAINS.find((c) => c.chainId === selectedChainId);

  return (
    <div className="relative flex items-center justify-between h-6 px-2 bg-[#007acc] text-white text-[11px] select-none shrink-0">
      {/* Left section */}
      <div className="flex items-center gap-3">
        {/* Workspace status */}
        <div className="flex items-center gap-1.5">
          <Circle
            className={cn(
              'w-2 h-2',
              activeWorkspace?.status === 'running'
                ? 'text-green-300 fill-green-300'
                : activeWorkspace?.status === 'error'
                  ? 'text-red-300 fill-red-300'
                  : 'text-yellow-300 fill-yellow-300',
            )}
          />
          <span>
            {activeWorkspace
              ? `${activeWorkspace.name} (${activeWorkspace.status})`
              : 'No workspace'}
          </span>
        </div>

        {/* Connection status */}
        <div className="flex items-center gap-1">
          {isConnected ? (
            <Wifi className="w-3 h-3 text-green-300" />
          ) : (
            <WifiOff className="w-3 h-3 text-red-300" />
          )}
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>

        {/* Branch info */}
        <button
          onClick={() => setActivePanel('git')}
          className="flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity"
          title="Source Control"
        >
          <GitBranch className="w-3 h-3" />
          <span>{gitBranch}</span>
        </button>

        {/* Command palette shortcut */}
        <button
          className="opacity-60 hover:opacity-100 transition-opacity text-[10px]"
          title="Command Palette"
        >
          Ctrl+Shift+P
        </button>

        {/* Lifecycle phase indicator */}
        <LifecycleIndicator />
      </div>

      {/* Right section */}
      <div className="flex items-center gap-3">
        {activeTab && (
          <>
            <span className="px-1.5 py-px bg-white/10 rounded text-[10px]">
              {activeTab.language}
            </span>
            <span>UTF-8</span>
          </>
        )}

        {/* Wallet status */}
        {walletConnected ? (
          <button
            onClick={() => setActivePanel('wallet')}
            className="flex items-center gap-1 px-1.5 py-px rounded hover:bg-white/10 transition-colors"
            title="Wallet connected"
          >
            <Wallet className="w-3 h-3 text-green-300" />
            <span className="text-[10px]">
              {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
            </span>
          </button>
        ) : (
          <button
            onClick={connectWallet}
            className="flex items-center gap-1 px-1.5 py-px rounded hover:bg-white/10 transition-colors opacity-70 hover:opacity-100"
            title="Connect wallet"
          >
            <Wallet className="w-3 h-3" />
            <span className="text-[10px]">No wallet</span>
          </button>
        )}

        {/* Notification bell */}
        <NotificationBell onClick={() => setShowNotifications(!showNotifications)} />

        {/* Chain selector */}
        {selectedChain && (
          <button
            onClick={() => setShowChainSelector(!showChainSelector)}
            className="flex items-center gap-1 px-1.5 py-px rounded hover:bg-white/10 transition-colors"
            title="Switch chain"
          >
            <span className="text-xs">{selectedChain.logo}</span>
            <span className="text-[10px]">{selectedChain.name}</span>
            <ChevronUp className="w-2.5 h-2.5 opacity-60" />
          </button>
        )}

        {/* RPC status */}
        {activeWorkspace?.rpcEndpoint && (
          <button
            onClick={() => setActivePanel('debug')}
            className="flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity"
            title="Open RPC Debugger"
          >
            <Zap className="w-3 h-3 text-yellow-300" />
            <span className="text-[10px]">RPC</span>
          </button>
        )}
      </div>

      {/* Chain selector popover */}
      <ChainSelector
        selectedChainId={selectedChainId}
        onSelect={setSelectedChainId}
        isOpen={showChainSelector}
        onClose={() => setShowChainSelector(false)}
      />

      {/* Notifications popover */}
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} />
    </div>
  );
}
